import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Multipleprint } from "@/lib/types";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkableType } from "../LinksManagement";
import { MultipleprintLinksView } from "./MultipleprintLinksView";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/** « Enregistrements liés › Multiple » : relation `relatedMultipleprint`. */
const MultipleLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [
    getPaginationRelatedMultipleprints,
    setPaginationRelatedMultipleprints,
  ] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedMultipleprint",
    getPaginationRelatedMultipleprints.pageSize,
    getPaginationRelatedMultipleprints.page *
      getPaginationRelatedMultipleprints.pageSize,
  );

  const { data: countRelatedMultipleprints } = useCountRelatedFields(id, type, [
    "relatedMultipleprint",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedMultipleprint",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedMultipleprint",
  );

  const [searchContent, setSearchContent] = useState("");

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedMultiple } = useGetNotRelatedResource<Multipleprint>({
    resultType: "multiple",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (multipleprintId: string) => {
      deleteRelation(multipleprintId);
    },
    [deleteRelation],
  );

  return (
    <MultipleprintLinksView
      storageKey="multiple-links-table"
      title={t("linkedMultipleprints")}
      linked={relatedFields ?? []}
      linkedPagination={{
        pagination: getPaginationRelatedMultipleprints,
        setPage: setPaginationRelatedMultipleprints,
        totalSize: countRelatedMultipleprints?.relatedMultipleprint ?? 0,
      }}
      candidates={notRelatedMultiple?.data ?? []}
      candidatesTotalSize={notRelatedMultiple?.totalSize ?? 0}
      candidatesPagination={getPagination}
      setCandidatesPagination={setPagination}
      searchContent={searchContent}
      setSearchContent={setSearchContent}
      onAdd={handleCreateRelation}
      onDelete={handleDeleteRelation}
    />
  );
};

export default MultipleLinks;
